/**
 * Finds the next available 30-minute-aligned time slot on a user's calendar.
 * Used to prefill event creation with a smart default time.
 */

import { deviceCalendarService } from '@/lib/calendar/DeviceCalendarService';

interface TimeSlot {
  date: string;  // YYYY-MM-DD
  time: string;  // HH:mm
}

export class NextAvailableSlotFinder {
  /**
   * Find the next free 1-hour slot rounded to the nearest 30 minutes.
   * Looks at today's events from the given calendars.
   * Returns { date, time } for the next open slot.
   */
  static async findNextSlot(calendarIds: string[]): Promise<TimeSlot> {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000);

    // Round current time UP to next 30-minute boundary
    const startMinutes = now.getHours() * 60 + now.getMinutes();
    const roundedMinutes = Math.ceil(startMinutes / 30) * 30;

    if (calendarIds.length === 0) {
      return this.minutesToSlot(today, roundedMinutes);
    }

    try {
      const events = await deviceCalendarService.listEvents(calendarIds, today, tomorrow);

      // Build list of busy intervals as minute ranges [start, end]
      const busyIntervals: [number, number][] = events
        .filter(e => !e.allDay && e.startDate)
        .map(e => {
          const start = new Date(e.startDate);
          const end = e.endDate ? new Date(e.endDate) : new Date(start.getTime() + 60 * 60 * 1000);

          // Only consider events that are today
          if (start.getDate() !== today.getDate()) return null;

          const startMin = start.getHours() * 60 + start.getMinutes();
          const endMin = end.getDate() === today.getDate()
            ? end.getHours() * 60 + end.getMinutes()
            : 24 * 60; // event extends past midnight
          return [startMin, endMin] as [number, number];
        })
        .filter((interval): interval is [number, number] => interval !== null)
        .sort((a, b) => a[0] - b[0]);

      // Search 30-min slots from rounded time until end of day
      for (let slotStart = roundedMinutes; slotStart + 60 <= 24 * 60; slotStart += 30) {
        const slotEnd = slotStart + 60;
        const isFree = !busyIntervals.some(
          ([busyStart, busyEnd]) => slotStart < busyEnd && slotEnd > busyStart,
        );
        if (isFree) {
          return this.minutesToSlot(today, slotStart);
        }
      }

      // No free slot today — return the rounded time anyway
      return this.minutesToSlot(today, roundedMinutes);
    } catch (err) {
      console.warn('[NextAvailableSlotFinder] Failed to query events:', err);
      return this.minutesToSlot(today, roundedMinutes);
    }
  }

  private static minutesToSlot(date: Date, minutes: number): TimeSlot {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return {
      date: `${y}-${m}-${d}`,
      time: `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`,
    };
  }
}
